/**
 * ThumbnailLoader - Centralized thumbnail loading with:
 * - Max 4 concurrent requests (prevents network flooding)
 * - LRU cache (100 items max)
 * - Request deduplication
 * - Priority queue (visible items first)
 * - Automatic decryption for encrypted thumbnails
 */

import { API_BASE_URL } from '../api/client';
import { vaultKeyManager } from './vaultKeyManager';

interface QueueItem {
  itemId: number;
  priority: number;
  resolve: (url: string | null) => void;
  reject: (error: Error) => void;
}

interface CacheEntry {
  url: string;
  timestamp: number;
}

class ThumbnailLoaderService {
  private cache = new Map<number, CacheEntry>();
  private queue: QueueItem[] = [];
  private loading = new Set<number>();
  private pendingPromises = new Map<number, Promise<string | null>>();

  private readonly MAX_CONCURRENT = 4;
  private readonly MAX_CACHE_SIZE = 100;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Load a thumbnail by item ID.
   * Returns cached version if available, otherwise queues fetch.
   */
  async load(itemId: number, priority: number = 0): Promise<string | null> {
    // Check cache first
    const cached = this.cache.get(itemId);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.url;
    }

    // Check if already loading this item
    const pending = this.pendingPromises.get(itemId);
    if (pending) {
      return pending;
    }

    // Create new promise and queue the request
    const promise = new Promise<string | null>((resolve, reject) => {
      this.queue.push({ itemId, priority, resolve, reject });
    });

    this.pendingPromises.set(itemId, promise);
    this.processQueue();

    return promise;
  }

  /**
   * Preload a thumbnail (low priority, doesn't block)
   */
  preload(itemId: number): void {
    if (this.cache.has(itemId) || this.loading.has(itemId)) {
      return;
    }
    this.load(itemId, -1).catch(() => {
      // Silently fail preloads
    });
  }

  /**
   * Check if thumbnail is cached
   */
  isCached(itemId: number): boolean {
    const cached = this.cache.get(itemId);
    return cached !== undefined && Date.now() - cached.timestamp < this.CACHE_TTL;
  }

  /**
   * Get cached URL if available
   */
  getCached(itemId: number): string | null {
    const cached = this.cache.get(itemId);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.url;
    }
    return null;
  }

  /**
   * Clear cache for specific item (e.g., after deletion)
   */
  invalidate(itemId: number): void {
    const cached = this.cache.get(itemId);
    if (cached) {
      URL.revokeObjectURL(cached.url);
      this.cache.delete(itemId);
    }
  }

  /**
   * Clear all cache
   */
  clearAll(): void {
    this.cache.forEach((entry) => URL.revokeObjectURL(entry.url));
    this.cache.clear();
  }

  private async processQueue(): Promise<void> {
    // Check if we can start more requests
    while (this.loading.size < this.MAX_CONCURRENT && this.queue.length > 0) {
      // Sort by priority (higher first)
      this.queue.sort((a, b) => b.priority - a.priority);

      const item = this.queue.shift();
      if (!item) break;

      // Skip if already cached (could have been loaded while waiting)
      if (this.isCached(item.itemId)) {
        item.resolve(this.getCached(item.itemId));
        this.pendingPromises.delete(item.itemId);
        continue;
      }

      this.loading.add(item.itemId);
      this.fetchThumbnail(item);
    }
  }

  private async fetchThumbnail(item: QueueItem): Promise<void> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/vault/items/${item.itemId}/thumbnail`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error(`Failed to load thumbnail: ${response.status}`);
      }

      const contentType = response.headers.get('content-type') || '';
      const arrayBuffer = await response.arrayBuffer();

      let blob: Blob;

      // Check if thumbnail is encrypted (application/octet-stream)
      if (contentType.includes('octet-stream')) {
        // Encrypted thumbnail - decrypt it
        if (!vaultKeyManager.isUnlocked()) {
          // Vault is locked, can't decrypt - return null
          item.resolve(null);
          return;
        }

        try {
          // Use smartDecrypt which auto-detects v1 (symmetric) vs v2 (ECDH) format
          const decryptedData = await vaultKeyManager.smartDecrypt(new Uint8Array(arrayBuffer));
          blob = new Blob([decryptedData], { type: 'image/jpeg' });
        } catch (decryptError) {
          console.warn('Failed to decrypt thumbnail:', decryptError);
          item.resolve(null);
          return;
        }
      } else {
        // Legacy unencrypted thumbnail
        blob = new Blob([arrayBuffer], { type: contentType || 'image/jpeg' });
      }

      const url = URL.createObjectURL(blob);

      // Add to cache
      this.addToCache(item.itemId, url);
      item.resolve(url);
    } catch (error) {
      item.resolve(null); // Resolve with null instead of rejecting
    } finally {
      this.loading.delete(item.itemId);
      this.pendingPromises.delete(item.itemId);
      this.processQueue(); // Process next items
    }
  }

  private addToCache(itemId: number, url: string): void {
    // A TTL refetch overwrites the entry — revoke the old URL first or the
    // blob leaks on every re-view past the TTL (BUG-047 #27)
    const existing = this.cache.get(itemId);
    if (existing && existing.url !== url) {
      URL.revokeObjectURL(existing.url);
    }

    // Enforce cache size limit (LRU eviction)
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      // Find oldest entry
      let oldestKey: number | null = null;
      let oldestTime = Infinity;

      this.cache.forEach((entry, key) => {
        if (entry.timestamp < oldestTime) {
          oldestTime = entry.timestamp;
          oldestKey = key;
        }
      });

      if (oldestKey !== null) {
        const oldEntry = this.cache.get(oldestKey);
        if (oldEntry) {
          URL.revokeObjectURL(oldEntry.url);
        }
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(itemId, { url, timestamp: Date.now() });
  }
}

// Singleton instance
export const thumbnailLoader = new ThumbnailLoaderService();
